import { getInsightsService } from '@app/promises/insights';
import { MainCategoryEnum } from '@insights/insights-data-provider';
import type { AnalyticsViewSource } from '@inter-ikea-storageone/shared/analytics';
import { InsightsCustomEventName } from '@inter-ikea-storageone/shared/analytics';

/**
 * Lightweight analytics reporters for landing page.
 *
 * This file is separate from reporters.ts to avoid importing Core dependencies.
 * reporters.ts imports coreInstanceStore and getAnalyticsSceneArticles, which pull in
 * the ~1-2MB Core bundle. These lightweight reporters bypass that entirely.
 *
 * They send events directly to InsightsService without scene article data.
 *
 * Only use these on the landing page where there's no 3D scene.
 * For the planner, use reporters.ts instead.
 */

/**
 * Reports toggle modal event (lightweight version without Core dependency).
 */
export const reportToggleModalLightweight = async (payload: {
  modal: string;
  visible: boolean;
  page: AnalyticsViewSource;
}) => {
  const insightsService = await getInsightsService();
  await insightsService.sendCustomEvent({
    event: InsightsCustomEventName.toggleModal,
    category: {
      mainCategory: MainCategoryEnum.interaction
    },
    payload
  });
};

/**
 * Reports book remote planning event (lightweight version without Core dependency).
 */
export const reportBookRemotePlanningLightweight = async (payload: {
  currentMenu: AnalyticsViewSource;
}) => {
  const insightsService = await getInsightsService();
  await insightsService.sendCustomEvent({
    event: InsightsCustomEventName.bookRemotePlanning,
    category: { mainCategory: MainCategoryEnum.conversion },
    payload
  });
};

/**
 * Reports design error screen event (lightweight version without Core dependency).
 */
export const reportDesignErrorScreenLightweight = async (payload: {
  vpcCode?: string;
  sprId?: string;
  designId?: string;
}) => {
  const insightsService = await getInsightsService();
  await insightsService.sendCustomEvent({
    // Cast to InsightsCustomEventName since this event isn't in the enum
    event: 'load_design_error_screen' as InsightsCustomEventName,
    category: { mainCategory: MainCategoryEnum.nonInteraction },
    payload
  });
};

/**
 * Reports web vitals metrics (lightweight version without Core dependency).
 *
 * This can be used on both landing page and planner - the useWebVitals hook
 * ensures it only reports once per session via sessionStorage.
 */
export const reportWebVitalsLightweight = async (payload: {
  largestContentfulPaint?: number;
  firstContentfulPaint?: number;
  timeToFirstByte?: number;
  cumulativeLayoutShift?: number;
}) => {
  const insightsService = await getInsightsService();
  await insightsService.sendCustomEvent({
    event: 'web_vitals' as InsightsCustomEventName,
    category: { mainCategory: MainCategoryEnum.nonInteraction },
    payload
  });
};
